import type { NightSkyResult } from "./night-sky-types";

const LOCATION_KEY = "sorakan-night-sky-location";

type SavedLocation = { latitude: number; longitude: number; name: string | null };

type GeocodeCandidate = {
  name: string;
  admin1: string | null;
  country: string;
  latitude: number;
  longitude: number;
};

let overlay: HTMLElement | null = null;
let currentLocation: SavedLocation | null = loadSavedLocation();
let result: NightSkyResult | null = null;
let busy = false;
let statusMessage = "";
let searchResults: GeocodeCandidate[] = [];

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function loadSavedLocation(): SavedLocation | null {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedLocation;
    if (!Number.isFinite(parsed.latitude) || !Number.isFinite(parsed.longitude)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveLocation(location: SavedLocation): void {
  currentLocation = location;
  localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
}

function ensureStyles(): void {
  if (document.querySelector("#night-sky-v030-styles")) return;
  const style = document.createElement("style");
  style.id = "night-sky-v030-styles";
  style.textContent = `
    .bottom-nav { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; }
    .night-sky-nav-icon { font-size: 22px; line-height: 1; }
    .night-sky-overlay { position: fixed; inset: 0; z-index: 1000; overflow-y: auto; background: #eef7fa; color: #173047; padding: env(safe-area-inset-top) 0 calc(82px + env(safe-area-inset-bottom)); }
    .night-sky-shell { width: min(680px, 100%); margin: 0 auto; padding: 20px 16px 28px; box-sizing: border-box; }
    .night-sky-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:16px; }
    .night-sky-header h1 { margin:0; font-size:1.7rem; }
    .night-sky-subtitle { margin:5px 0 0; color:#607486; font-size:.92rem; }
    .night-sky-location { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 16px; background:#fff; border-radius:18px; box-shadow:0 5px 18px rgba(39,74,99,.08); margin-bottom:14px; }
    .night-sky-location strong { display:block; font-size:1rem; }
    .night-sky-location button, .night-sky-small-button { border:1px solid #bdd1dc; background:#f7fbfd; color:#31566d; border-radius:999px; padding:8px 12px; font:inherit; }
    .night-sky-index-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:12px; }
    .night-sky-index-card { background:#fff; border-radius:20px; padding:16px 12px; text-align:center; box-shadow:0 5px 18px rgba(39,74,99,.08); }
    .night-sky-index-label { font-weight:700; font-size:.96rem; }
    .night-sky-index-number { display:block; font-size:2.7rem; line-height:1.05; font-weight:800; margin:8px 0; letter-spacing:-.04em; }
    .night-sky-best { color:#617989; font-size:.82rem; line-height:1.45; }
    .night-sky-comment { background:linear-gradient(135deg,#fffdf2,#fff); border-radius:20px; padding:17px 18px; margin:12px 0; box-shadow:0 5px 18px rgba(39,74,99,.07); }
    .night-sky-comment h2, .night-sky-card h2 { font-size:1.02rem; margin:0 0 8px; }
    .night-sky-comment p { margin:0; line-height:1.75; }
    .night-sky-card { background:#fff; border-radius:20px; padding:16px; margin:12px 0; box-shadow:0 5px 18px rgba(39,74,99,.08); }
    .night-sky-chart { width:100%; height:auto; display:block; overflow:visible; }
    .night-sky-chart text { font-family:inherit; font-size:10px; fill:#617989; }
    .night-sky-chart .value { font-size:11px; fill:#274b61; font-weight:700; }
    .night-sky-targets { display:flex; flex-wrap:wrap; gap:7px; margin-top:10px; }
    .night-sky-target { background:#eef5f8; border-radius:999px; padding:7px 10px; font-size:.84rem; }
    .night-sky-moon { text-align:center; }
    .night-sky-moon-emoji { display:block; font-size:4rem; margin:2px 0 4px; }
    .night-sky-moon-title { font-weight:700; margin-bottom:14px; }
    .night-sky-moon-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px 12px; text-align:left; }
    .night-sky-moon-grid div { background:#f6fafc; padding:10px 11px; border-radius:13px; }
    .night-sky-moon-grid small { display:block; color:#718693; margin-bottom:3px; }
    .night-sky-loading { text-align:center; padding:48px 20px; color:#5f7584; }
    .night-sky-location-panel { background:#fff; border-radius:20px; padding:18px; box-shadow:0 5px 18px rgba(39,74,99,.08); }
    .night-sky-location-panel input { width:100%; box-sizing:border-box; border:1px solid #c6d5de; border-radius:12px; padding:12px; font:inherit; margin:12px 0 8px; }
    .night-sky-location-panel button { width:100%; border:0; border-radius:13px; padding:12px; font:inherit; font-weight:700; background:#315f78; color:#fff; margin-top:7px; }
    .night-sky-search-result { text-align:left !important; background:#f2f7fa !important; color:#274b61 !important; font-weight:500 !important; }
    .night-sky-status { margin:12px 0 0; color:#735c42; line-height:1.6; }
    .night-sky-source { text-align:center; color:#718693; font-size:.76rem; margin:18px 0 5px; }
    .night-sky-overlay-nav { position:fixed; z-index:1001; bottom:0; left:0; right:0; display:grid; grid-template-columns:repeat(4,1fr); padding:6px 6px calc(6px + env(safe-area-inset-bottom)); background:rgba(255,255,255,.96); border-top:1px solid #d7e3e9; backdrop-filter:blur(12px); }
    .night-sky-overlay-nav button { border:0; background:transparent; color:#6b7d88; padding:7px 2px 4px; font:inherit; font-size:.72rem; }
    .night-sky-overlay-nav button span { display:block; font-size:22px; line-height:1.1; margin-bottom:2px; }
    .night-sky-overlay-nav button.active { color:#245a75; font-weight:700; }
    @media (max-width: 360px) {
      .night-sky-shell { padding-left:12px; padding-right:12px; }
      .night-sky-index-number { font-size:2.35rem; }
      .night-sky-index-card { padding-left:8px; padding-right:8px; }
      .night-sky-moon-grid { grid-template-columns:1fr; }
    }
  `;
  document.head.appendChild(style);
}

function formatTime(iso: string | null, timezone: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));
}

function formatMoonEvent(iso: string | null, timezone: string): string {
  if (!iso) return "—";
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: timezone,
    month: "numeric",
    day: "numeric",
  }).format(new Date(iso));
  return `${parts} ${formatTime(iso, timezone)}`;
}

function bestRange(start: string, end: string, timezone: string): string {
  return `${formatTime(start, timezone)}〜${formatTime(end, timezone)}`;
}

function sampledPoints(data: NightSkyResult["star"]["hourly"]): NightSkyResult["star"]["hourly"] {
  if (data.length <= 6) return data;
  const last = data.length - 1;
  const indices = Array.from({ length: 6 }, (_, index) => Math.round((last * index) / 5));
  return indices.filter((value, index) => index === 0 || value !== indices[index - 1]).map((index) => data[index]);
}

function chart(
  data: NightSkyResult["star"]["hourly"],
  timezone: string,
  selector: (point: NightSkyResult["star"]["hourly"][number]) => number | null,
): string {
  const points = sampledPoints(data).map((point) => ({ point, value: selector(point) })).filter((item) => item.value !== null) as Array<{
    point: NightSkyResult["star"]["hourly"][number];
    value: number;
  }>;
  if (points.length === 0) return `<p>この時間帯は表示できる指数がありません。</p>`;

  const left = 20;
  const right = 300;
  const top = 18;
  const bottom = 78;
  const x = (index: number) => (points.length === 1 ? 160 : left + ((right - left) * index) / (points.length - 1));
  const y = (value: number) => bottom - ((bottom - top) * value) / 100;
  const polyline = points.map((item, index) => `${x(index)},${y(item.value)}`).join(" ");
  const dots = points
    .map(
      (item, index) =>
        `<circle cx="${x(index)}" cy="${y(item.value)}" r="3.5"></circle><text class="value" x="${x(index)}" y="${y(item.value) - 8}" text-anchor="middle">${item.value}</text><text x="${x(index)}" y="103" text-anchor="middle">${formatTime(item.point.time, timezone).replace(":00", "時")}</text>`,
    )
    .join("");
  return `<svg class="night-sky-chart" viewBox="0 0 320 112" role="img" aria-label="時間による指数の変化"><line x1="20" y1="78" x2="300" y2="78" stroke="#dbe6eb"/><line x1="20" y1="48" x2="300" y2="48" stroke="#edf2f4"/><polyline points="${polyline}" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>${dots}</svg>`;
}

function targetTags(items: NightSkyResult["star"]["constellations"]): string {
  if (items.length === 0) return "";
  return `<div class="night-sky-targets">${items
    .map((item) => `<span class="night-sky-target">${escapeHtml(item.direction)}：${escapeHtml(item.name)}</span>`)
    .join("")}</div>`;
}

function renderLocationChooser(): string {
  const results = searchResults
    .map(
      (item, index) =>
        `<button type="button" class="night-sky-search-result" data-night-action="select-place" data-index="${index}">${escapeHtml(
          [item.name, item.admin1, item.country].filter(Boolean).join(" / "),
        )}</button>`,
    )
    .join("");
  return `<div class="night-sky-shell">
    <header class="night-sky-header"><div><h1>今夜の空</h1><p class="night-sky-subtitle">星と月の見え方をのぞいてみよう</p></div><button class="night-sky-small-button" data-night-action="close">閉じる</button></header>
    <section class="night-sky-location-panel">
      <h2>見る場所を選ぶ</h2>
      <p>場所がわかると、今夜の星と月の見え方を計算できます。</p>
      <button type="button" data-night-action="current-location">現在地を使う</button>
      <form data-night-form="place-search"><input name="place" required placeholder="市区町村・地名を入力" aria-label="地名"><button type="submit">地名から探す</button></form>
      ${results}
      ${statusMessage ? `<p class="night-sky-status">${escapeHtml(statusMessage)}</p>` : ""}
    </section>
  </div>${renderOverlayNav()}`;
}

function renderLoading(): string {
  return `<div class="night-sky-shell"><header class="night-sky-header"><div><h1>今夜の空</h1><p class="night-sky-subtitle">星と月の見え方をのぞいてみよう</p></div></header><div class="night-sky-loading">今夜の空を調べています…</div></div>${renderOverlayNav()}`;
}

function renderResult(): string {
  if (!result) return renderLocationChooser();
  const timezone = result.timezone;
  const moonIndex = result.moonViewing?.index;
  const moonBest = result.moonViewing ? bestRange(result.moonViewing.bestStart, result.moonViewing.bestEnd, timezone) : "月がほとんど見えない夜";
  const moonPosition = result.moon.bestDirection
    ? `${formatTime(result.moonViewing?.bestMidpoint ?? null, timezone)}ごろ ${result.moon.bestDirection}の${result.moon.bestAltitudeLabel}`
    : "—";

  return `<div class="night-sky-shell">
    <header class="night-sky-header"><div><h1>今夜の空</h1><p class="night-sky-subtitle">星と月の見え方をのぞいてみよう</p></div></header>
    <section class="night-sky-location"><div><small>見る場所</small><strong>${escapeHtml(result.location.name || "現在地")}</strong></div><button type="button" data-night-action="change-location">場所を変更</button></section>
    <div class="night-sky-index-grid">
      <section class="night-sky-index-card"><div class="night-sky-index-label">🌌 星空指数</div><span class="night-sky-index-number">${result.star.index}</span><div class="night-sky-best">見ごろ<br><strong>${bestRange(result.star.bestStart, result.star.bestEnd, timezone)}</strong></div></section>
      <section class="night-sky-index-card"><div class="night-sky-index-label">${result.moon.emoji} お月見指数</div><span class="night-sky-index-number">${moonIndex ?? "—"}</span><div class="night-sky-best">見ごろ<br><strong>${escapeHtml(moonBest)}</strong></div></section>
    </div>
    <section class="night-sky-comment"><h2>空からのひとこと</h2><p>${escapeHtml(result.comment)}</p></section>
    <section class="night-sky-card"><h2>星空の見え方</h2>${chart(result.star.hourly, timezone, (point) => point.starIndex)}${targetTags(result.star.constellations)}${targetTags(result.star.firstMagnitudeStars)}</section>
    <section class="night-sky-card"><h2>お月見の見え方</h2>${chart(result.star.hourly, timezone, (point) => point.moonViewingIndex)}</section>
    <section class="night-sky-card night-sky-moon"><span class="night-sky-moon-emoji">${result.moon.emoji}</span><div class="night-sky-moon-title">${escapeHtml(result.moon.phaseLabel)}</div><div class="night-sky-moon-grid">
      <div><small>月の明るさ</small><strong>${result.moon.illuminationPct}%</strong></div>
      <div><small>月齢</small><strong>${result.moon.ageDays.toFixed(1)}</strong></div>
      <div><small>月の出</small><strong>${formatMoonEvent(result.moon.moonrise, timezone)}</strong></div>
      <div><small>月の入り</small><strong>${formatMoonEvent(result.moon.moonset, timezone)}</strong></div>
      <div style="grid-column:1/-1"><small>見ごろの月</small><strong>${escapeHtml(moonPosition)}</strong></div>
    </div></section>
    <p class="night-sky-source">気象：Open-Meteo / JMA　・　月情報：Sorakan内の天文計算</p>
  </div>${renderOverlayNav()}`;
}

function renderOverlayNav(): string {
  return `<nav class="night-sky-overlay-nav" aria-label="メインメニュー"><button data-night-action="home"><span>⌂</span>ホーム</button><button class="active"><span>☾</span>今夜</button><button data-night-action="history"><span>◷</span>履歴</button><button data-night-action="settings"><span>≡</span>設定</button></nav>`;
}

function render(): void {
  if (!overlay) return;
  overlay.innerHTML = busy ? renderLoading() : result ? renderResult() : renderLocationChooser();
}

async function apiError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    return body.error || "処理に失敗しました。";
  } catch {
    return "処理に失敗しました。";
  }
}

async function loadNightSky(): Promise<void> {
  if (!currentLocation) return;
  busy = true;
  statusMessage = "";
  render();
  try {
    const response = await fetch("/api/night-sky", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ position: currentLocation }),
    });
    if (!response.ok) throw new Error(await apiError(response));
    result = (await response.json()) as NightSkyResult;
  } catch (error) {
    result = null;
    statusMessage = error instanceof Error ? error.message : "今夜の空を取得できませんでした。";
  } finally {
    busy = false;
    render();
  }
}

async function resolveLocationName(latitude: number, longitude: number): Promise<string | null> {
  try {
    const query = new URLSearchParams({ latitude: String(latitude), longitude: String(longitude) });
    const response = await fetch(`/api/location-context?${query.toString()}`);
    if (!response.ok) return null;
    const body = (await response.json()) as { name?: string | null };
    return body.name ?? null;
  } catch {
    return null;
  }
}

async function useCurrentLocation(): Promise<void> {
  if (!navigator.geolocation) {
    statusMessage = "この端末では現在地を取得できません。地名から選んでください。";
    render();
    return;
  }
  busy = true;
  render();
  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const name = await resolveLocationName(latitude, longitude);
      saveLocation({ latitude, longitude, name: name ?? "現在地" });
      result = null;
      busy = false;
      await loadNightSky();
    },
    () => {
      busy = false;
      statusMessage = "現在地を取得できませんでした。地名から選ぶこともできます。";
      render();
    },
    { enableHighAccuracy: false, timeout: 8_000, maximumAge: 10 * 60_000 },
  );
}

async function searchPlace(name: string): Promise<void> {
  busy = true;
  render();
  try {
    const response = await fetch(`/api/geocode?${new URLSearchParams({ name }).toString()}`);
    if (!response.ok) throw new Error(await apiError(response));
    const body = (await response.json()) as { results?: GeocodeCandidate[] };
    searchResults = body.results ?? [];
    statusMessage = searchResults.length === 0 ? "候補が見つかりませんでした。" : "候補を選んでください。";
  } catch (error) {
    statusMessage = error instanceof Error ? error.message : "地名を検索できませんでした。";
  } finally {
    busy = false;
    render();
  }
}

function closeOverlay(): void {
  overlay?.remove();
  overlay = null;
}

function openNightSky(): void {
  ensureStyles();
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.className = "night-sky-overlay";
  overlay.addEventListener("click", (event) => void handleOverlayClick(event));
  overlay.addEventListener("submit", (event) => void handleOverlaySubmit(event));
  document.body.appendChild(overlay);
  render();
  if (currentLocation) void loadNightSky();
  else void useCurrentLocation();
}

async function handleOverlayClick(event: Event): Promise<void> {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-night-action]");
  if (!target || busy) return;
  const action = target.dataset.nightAction;
  if (action === "close" || action === "home") closeOverlay();
  if (action === "change-location") {
    result = null;
    searchResults = [];
    statusMessage = "";
    render();
  }
  if (action === "current-location") await useCurrentLocation();
  if (action === "select-place") {
    const index = Number(target.dataset.index);
    const candidate = searchResults[index];
    if (!candidate) return;
    const name = [candidate.admin1, candidate.name].filter(Boolean).join(" ") || candidate.name;
    saveLocation({ latitude: candidate.latitude, longitude: candidate.longitude, name });
    result = null;
    searchResults = [];
    await loadNightSky();
  }
  if (action === "history" || action === "settings") {
    closeOverlay();
    const underlying = document.querySelector<HTMLElement>(`[data-action="nav-${action}"]`);
    underlying?.click();
  }
}

async function handleOverlaySubmit(event: Event): Promise<void> {
  const form = (event.target as HTMLElement).closest<HTMLFormElement>("form[data-night-form='place-search']");
  if (!form) return;
  event.preventDefault();
  const data = new FormData(form);
  const place = String(data.get("place") ?? "").trim();
  if (place) await searchPlace(place);
}

function enhanceBottomNav(): void {
  ensureStyles();
  document.querySelectorAll<HTMLElement>(".bottom-nav").forEach((nav) => {
    if (nav.querySelector("[data-night-sky-nav]")) return;
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.nightSkyNav = "1";
    button.innerHTML = `<span class="night-sky-nav-icon" aria-hidden="true">☾</span>今夜`;
    button.addEventListener("click", openNightSky);
    const historyButton = nav.querySelector('[data-action="nav-history"]');
    nav.insertBefore(button, historyButton);
  });
}

const observer = new MutationObserver(enhanceBottomNav);
observer.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener("DOMContentLoaded", enhanceBottomNav);
enhanceBottomNav();
